<!-- Chat question message that renders the agent's prompt and the matching typed input widget. -->

<script lang="ts" setup>
import type { DateInput } from '@nzyme/types/Date.js';
import type { Json } from '@nzyme/utils/toJson.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { Reveal } from '@nzyme/vue/components/Reveal.jsx';

import type { FormInputConfig, FormInputValue } from '@sigma/common/forms/FormInput.js';

import ChatAgentAvatar from '../ChatAgentAvatar.vue';
import ChatMessage from '../ChatMessage.vue';
import ChatBirthDateInput from './ChatBirthDateInput.vue';
import ChatBloodPressureInput from './ChatBloodPressureInput.vue';
import ChatGenderInput from './ChatGenderInput.vue';
import ChatMultiSelectInput from './ChatMultiSelectInput.vue';
import * as l from './ChatQuestion.loc.js';
import ChatScalarMeasurementInput from './ChatScalarMeasurementInput.vue';
import ChatScaleInput from './ChatScaleInput.vue';
import ChatSingleSelectInput from './ChatSingleSelectInput.vue';

interface Props {
  align?: 'left' | 'right';
  /** The input configuration that determines which question type to render. */
  config: FormInputConfig;
  /** The date when this question event occurred, displayed as a timestamp on the message. */
  timestamp: DateInput;
  /** Whether the input form should be visible (typically only for the last question). */
  showInput: boolean;
}

interface Emits {
  submit: [value: Json<FormInputValue>];
}

defineProps<Props>();
defineEmits<Emits>();

const emitAsync = useEmitAsync<Emits>();
</script>

<template>
  <!-- Question message from agent -->
  <ChatMessage
    color="white"
    :align="align"
    :timestamp="timestamp"
  >
    <template #avatar>
      <ChatAgentAvatar />
    </template>

    <template #content>
      {{ config.question }}
    </template>
  </ChatMessage>

  <!-- Default slot: input form, can be overridden -->
  <slot>
    <Reveal>
      <!--
        Structured inputs render their inline widget here. TEXT questions are intentionally excluded:
        they are answered through the main chat composer (which switches into "answer mode" when a text
        question is pending), so nothing is rendered inline for them.
      -->
      <div
        v-if="showInput && config.type !== 'TEXT'"
        tid="chat-question-input"
        :class="css.container"
      >
        <div
          v-if="config.help"
          :class="css.helpText"
        >
          {{ config.help }}
        </div>

        <ChatSingleSelectInput
          v-if="config.type === 'SINGLE_SELECT'"
          :config="config"
          @submit="emitAsync('submit', $event)"
        />
        <ChatMultiSelectInput
          v-else-if="config.type === 'MULTI_SELECT'"
          :config="config"
          @submit="emitAsync('submit', $event)"
        />
        <ChatScaleInput
          v-else-if="config.type === 'SCALE'"
          :config="config"
          @submit="emitAsync('submit', $event)"
        />
        <ChatBirthDateInput
          v-else-if="config.type === 'BIRTH_DATE'"
          :config="config"
          @submit="emitAsync('submit', $event)"
        />
        <ChatGenderInput
          v-else-if="config.type === 'GENDER'"
          :config="config"
          @submit="emitAsync('submit', $event)"
        />
        <ChatScalarMeasurementInput
          v-else-if="config.type === 'MEASUREMENT'"
          :kind="config.kind"
          :error-message="l.enterMeasurement"
          @submit="emitAsync('submit', $event)"
        />
        <ChatBloodPressureInput
          v-else-if="config.type === 'BLOOD_PRESSURE'"
          :config="config"
          @submit="emitAsync('submit', $event)"
        />
      </div>
    </Reveal>
  </slot>
</template>

<style lang="scss" module="css">
.container {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
  background: $color-white;
  border-radius: $border-radius-10;
  padding: $spacing-12;
  max-width: 600px;
}

.helpText {
  font-size: $font-size-14;
  color: $color-gray-600;
  padding: $spacing-04;
}
</style>
